import { useState } from 'react';

/**
 * TogetherPrompt — shown after adding an item when the user is in a pod.
 * Replaces the search results with a centered prompt.
 *
 * For 2-person pods: simple "Just me" / "Together with [Name]" buttons.
 * For 3+ person pods: toggle-able member buttons to pick who you were with, then "Done".
 *
 * @param {Object} props
 * @param {string} props.itemTitle - Title of the just-added item
 * @param {Array} props.members - Pod members [{ userId, displayName, emoji }] (excluding current user)
 * @param {string} props.podId - Active pod ID
 * @param {Function} props.onJustMe - Called when user picks "Just me"
 * @param {Function} props.onTogether - Called with (podId, selectedUserIds[]) when user confirms
 */
export default function TogetherPrompt({ itemTitle, members = [], podId, onJustMe, onTogether }) {
    const [selected, setSelected] = useState(new Set());

    if (members.length === 0) return null;

    const toggle = (userId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    // Simple mode for 1 other member (couples)
    if (members.length === 1) {
        const other = members[0];
        return (
            <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center">
                <div className="text-3xl mb-3">✓</div>
                <h3 className="text-base font-display font-semibold text-text-primary mb-1">
                    Added!
                </h3>
                <p className="text-sm text-text-secondary font-body mb-6 max-w-[240px]">
                    {itemTitle}
                </p>
                <p className="text-xs text-text-tertiary font-body mb-3">
                    Who was this with?
                </p>
                <div className="flex flex-col items-center gap-2 w-full max-w-[240px]">
                    <button
                        onClick={onJustMe}
                        className="w-full px-4 py-2.5 rounded-md text-sm font-medium bg-bg-secondary border border-border-subtle text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
                    >
                        Just me
                    </button>
                    <button
                        onClick={() => onTogether?.(podId, [other.userId])}
                        className="w-full px-4 py-2.5 rounded-md text-sm font-medium bg-accent-primary text-text-inverse hover:brightness-90 transition-all"
                    >
                        Together with {other.displayName || other.name}
                    </button>
                </div>
            </div>
        );
    }

    // Multi-member mode: toggleable member chips + Done
    return (
        <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center">
            <div className="text-3xl mb-3">✓</div>
            <h3 className="text-base font-display font-semibold text-text-primary mb-1">
                Added!
            </h3>
            <p className="text-sm text-text-secondary font-body mb-6 max-w-[280px]">
                {itemTitle}
            </p>
            <p className="text-xs text-text-tertiary font-body mb-3">
                Who was this with?
            </p>

            <div className="flex flex-wrap justify-center gap-2 mb-5 max-w-[280px]">
                {members.map((m) => {
                    const isSelected = selected.has(m.userId);
                    return (
                        <button
                            key={m.userId}
                            onClick={() => toggle(m.userId)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                isSelected
                                    ? 'bg-accent-primary/20 border border-accent-primary text-accent-primary'
                                    : 'bg-bg-secondary border border-border-subtle text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                            }`}
                        >
                            <span className="text-base">{m.emoji}</span>
                            {m.displayName || m.name}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-col items-center gap-2 w-full max-w-[240px]">
                {selected.size > 0 && (
                    <button
                        onClick={() => onTogether?.(podId, [...selected])}
                        className="w-full px-4 py-2.5 rounded-md text-sm font-medium bg-accent-primary text-text-inverse hover:brightness-90 transition-all"
                    >
                        Done
                    </button>
                )}
                <button
                    onClick={onJustMe}
                    className="w-full px-4 py-2.5 rounded-md text-sm font-medium bg-bg-secondary border border-border-subtle text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
                >
                    Just me
                </button>
            </div>
        </div>
    );
}
